import { IDTransactionLog, IProfile, TransactionStatus, TrxLogType } from 'realhaus-sdk';
import { firebaseConfig } from '../configs/firebase.config';
import StringUtils from './string';
import { User } from 'firebase/auth';


export const getRequestedPayouts = async (token: string, cursor?: { id: string, timestamp: number }) => {

  try {
    let url = `${firebaseConfig.cloudFunctionAdminApiURL}/payouts/requests`;
    if (cursor) {
      url = `${url}?cursor=${JSON.stringify(cursor)}`;
    }
    const response = await fetch(encodeURI(url),
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'GET',
      }
    );
    if (response.status === 200) {
      const jsonData = await response.json();
      const responseData = jsonData as { trxLogs: IDTransactionLog[], cursor?: { id: string, timestamp: number } }
      return responseData;
    } else {
      StringUtils.consoleLogResponse(response);
    }

  } catch (err) {
    console.error(err);
    throw new Error('Failed to get payouts');
  }

}

export const getRequestedTransactions = async (token: string, cursor?: { id: string, timestamp: number }, trxLogId?: string, statuses?: TransactionStatus[], types?: TrxLogType[]) => {

  try {
    const baseUrl = `${firebaseConfig.cloudFunctionAdminApiURL}/transactionLogs`;

    const url = new URL(baseUrl);

    if (cursor) {
      url.searchParams.append('cursor', encodeURIComponent(JSON.stringify(cursor)));
    }

    if (trxLogId) {
      url.searchParams.append('trxLogId', trxLogId);
    }

    if (statuses && statuses.length > 0) {
      const status = statuses.join(',')
      url.searchParams.append('statuses', status)
    }

    if (types && types.length > 0) {
      const type = types.join(',')
      url.searchParams.append('types', type)
    }


    const response = await fetch(url.toString(),
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'GET',
      }
    );
    if (response.status === 200) {
      const jsonData = await response.json();
      const responseData = jsonData as { trxLogs: IDTransactionLog[], cursor?: { id: string, timestamp: number } }
      return responseData;
    } else {
      StringUtils.consoleLogResponse(response);
    }

  } catch (err) {
    console.error(err);
    throw new Error('Failed to get transaction logs');
  }

}

export const findAssociatedRefundTransaction = async (token: string, trxLogId: string) => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/payment/findRefund`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify({ trxLogId })
      }
    );
    if (response.status === 200) {
      return (await response.json()) as IDTransactionLog[];
    }
  } catch (err) {
    console.error('>>', err);
    throw new Error('Failed to find associated refund transaction ');
  }
}

export const refundTransaction = async (token: string, trxLogId: string) => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/payment/refund`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify({ trxLogId })
      }
    );
    return response.status === 201
  } catch (err) {
    console.error(err);
    throw new Error(`Failed to refund transaction - ${trxLogId}`);
  }
}

export const getUserProfile = async (token: string, userId: string) => {

  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/users/profile?uid=${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      method: 'GET',
    })

    if (response.ok) {
      const responseData = (await response.json()) as IProfile;
      return responseData;
    } else {
      StringUtils.consoleLogResponse(response)
    }
  } catch (err) {
    console.error(err);
    throw new Error('Failed to get user profile');
  }
}

export type BankInfoResponse = {
  email: string,
  bank: {
    institution: string
    eftAccount: {
      account: string;
      accountId: string;
      institution: string;
      branch: string;
    },
    accountDetails: {
      account_id: string;
      balances: any;
      mask?: string;
      name: string;
      official_name?: string;
      subtype: any;
      type: any;
    }
  }
}

export const requestBankInfo = async (token: string, userId: string) => {

  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/wallet/bank-accounts?uid=${userId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'GET',
      }
    );
    if (response.status === 200) {
      const responseData = (await response.json()) as (BankInfoResponse)
      return responseData;
    } else {
      StringUtils.consoleLogResponse(response)
    }
  } catch (err) {

    console.error(err);
    throw new Error('Failed to get payouts');

  }

}


export const updatePaymentStatusToPaid = async (token: string, trxLogId: string) => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/payouts/complete`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify({ id: trxLogId })
      }
    );
    return response.status === 200
  } catch (err) {
    console.error(err);
    throw new Error('Failed to mark payout request as paid');
  }
}

export const createPayout = async (token: string, uid: string) => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/payouts/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ uid })
    });

    if (!response.ok) {
      throw new Error('Failed to create payout');
    }

    return;
  } catch (error) {
    console.error('Error creating payout:', error);
    throw error;
  }
};

export type UserListUser = {
  uid: string,
  email: string,
  emailVerified: boolean,
  firstname: string,
  lastname: string,
  isFlagged?: boolean,
  walletBalance: number,
  createdAt: number,
}

export type UserListResponse = {
  users: UserListUser[],
  cursor?: string
}

export const getUsers = async (token: string, cursor?: string) => {
  try {
    const url = new URL(`${firebaseConfig.cloudFunctionAdminApiURL}/users/list`);
    if (!!cursor) {
      url.searchParams.append('cursor', cursor);
    }
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    return (await response.json() as UserListResponse);
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const flagUser = async (token: string, uid: string) => {
  try {
    const url = new URL(`${firebaseConfig.cloudFunctionAdminApiURL}/users/flag`);

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      method: 'POST',
      body: JSON.stringify({ uid })
    });
    if (!response.ok) {
      throw new Error('Failed to flag user');
    }
    return response.ok;
  } catch (error) {
    console.error('Error Failed to flag user:', error);
    throw error;
  }
};

export const unFlagUser = async (token: string, uid: string) => {
  try {
    const url = new URL(`${firebaseConfig.cloudFunctionAdminApiURL}/users/unflag`);

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      method: 'POST',
      body: JSON.stringify({ uid })
    });

    if (!response.ok) {
      throw new Error('Failed to flag user');
    }

    return response.ok;
  } catch (error) {
    console.error('Error Failed to flag user:', error);
    throw error;
  }
};


export type MigrationRunStatus = 'queued' | 'running' | 'success' | 'failed' | 'interrupted';

export interface MigrationInfo {
  id: string;
  timestamp: number;
  description: string;
  status: 'applied' | 'pending' | 'running';
  appliedAt?: number;
  appliedBy?: string;
  /** Applied before migrations were recorded by id, so when and by whom isn't known. */
  legacy?: boolean;
  /** Pending, with a timestamp below the last one applied. */
  outOfOrder?: boolean;
}

export interface MigrationRunResult {
  id: string;
  status: 'applied' | 'failed' | 'skipped';
  error?: string;
  finishedAt: number;
}

export interface MigrationRun {
  id: string;
  status: MigrationRunStatus;
  requestedBy: string;
  requestedAt: number;
  startedAt?: number;
  finishedAt?: number;
  pending: string[];
  current?: string;
  results: MigrationRunResult[];
  error?: string;
}

export interface MigrationStatusResponse {
  projectId: string;
  pendingCount: number;
  migrations: MigrationInfo[];
  run?: MigrationRun;
}

export interface MigrationLock {
  runId: string;
  by: string;
  since: number;
  expiresAt: number;
}

export type StartMigrationRunResponse =
  | { status: 'noop' }
  | { status: 'queued', runId: string }
  | { status: 'running', running: MigrationLock };

export const getMigrationStatus = async (token: string) => {
  const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/migration/status`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'GET',
  });
  if (!response.ok) throw new Error(`Failed to get migration status: ${response.status}`);
  return (await response.json()) as MigrationStatusResponse;
}

/** Queues a run of every pending migration. 409 (`running`) means a run is already queued or in progress. */
export const startMigrationRun = async (token: string) => {
  const response = await fetch(`${firebaseConfig.cloudFunctionAdminApiURL}/migration/migrateDb`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'POST',
  });
  if ([200, 202, 409].includes(response.status)) {
    return (await response.json()) as StartMigrationRunResponse;
  }
  throw new Error(`Failed to start the migration run: ${response.status}`);
}
